import { Link } from '@tanstack/react-router'
import { ArrowLeft, Edit } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useFnsMode } from '@/features/forms-and-surveys/hooks'
import { Survey } from '@/features/forms-and-surveys/types'
import {
  getSurveyTypeColor,
  getTranslatedSurveyTypeLabel,
} from '@/features/forms-and-surveys/utils'

interface SurveyDetailProps {
  data?: Survey
  loading: boolean
}

function FnsDetail({ data, loading }: SurveyDetailProps) {
  const mode = useFnsMode()
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center gap-4'>
          <Button variant='ghost' size='sm' asChild className='-ml-2'>
            <Link to={mode === 'survey' ? '/surveys' : '/forms'}>
              <ArrowLeft className='mr-2 h-4 w-4' />
              {t('common.back')} {t(`navigation.${mode}s`)}
            </Link>
          </Button>
        </div>
        <Skeleton className='h-8 w-64' />
        <div className='grid gap-6 md:grid-cols-2'>
          <Skeleton className='h-48' />
          <Skeleton className='h-48' />
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center gap-4'>
          <Button variant='ghost' size='sm' asChild className='-ml-2'>
            <Link to={mode === 'survey' ? '/surveys' : '/forms'}>
              <ArrowLeft className='mr-2 h-4 w-4' />
              {t('common.back')} {t(`navigation.${mode}s`)}
            </Link>
          </Button>
        </div>
        <div className='text-center'>
          <h2 className='text-xl font-semibold'>
            {t(`surveys.${mode}NotFound`)}
          </h2>
          <p className='text-muted-foreground'>
            {t(`surveys.${mode}NotFoundDescription`)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <Button variant='ghost' size='sm' asChild className='-ml-2'>
            <Link to={mode === 'survey' ? '/surveys' : '/forms'}>
              <ArrowLeft className='mr-2 h-4 w-4' />
              {t('common.back')} {t(`navigation.${mode}s`)}
            </Link>
          </Button>

          <Button variant='default' size='sm' asChild>
            <Link
              to={
                mode === 'survey'
                  ? '/surveys/$surveyId/edit'
                  : '/forms/$formId/edit'
              }
              params={
                mode === 'survey' ? { surveyId: data.id } : { formId: data.id }
              }
            >
              <Edit className='mr-2 h-4 w-4' />
              {t(`surveys.edit${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}
            </Link>
          </Button>
        </div>

        <div className='space-y-6'>
          {/* Survey Overview Card */}
          <Card className='overflow-hidden'>
            <CardHeader>
              <CardTitle className='text-2xl font-bold tracking-tight'>
                {data.title}
              </CardTitle>
              {data.description && (
                <CardDescription className='text-balance text-base'>
                  {data.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className='flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between'>
                {/* Left side - Badges */}
                <div className='flex flex-wrap items-center gap-6'>
                  <div className='flex items-center gap-6'>
                    {mode === 'survey' ? (
                      <div className='flex flex-col items-start gap-2'>
                        <span className='text-xs font-medium text-muted-foreground'>
                          {t('common.type')}
                        </span>
                        <Badge
                          className={`${getSurveyTypeColor(data.type || '')} px-4 py-2 text-sm font-semibold shadow-sm`}
                        >
                          {getTranslatedSurveyTypeLabel(t, data.type || '')}
                        </Badge>
                      </div>
                    ) : null}
                    <div className='flex flex-col items-start gap-2'>
                      <span className='text-xs font-medium text-muted-foreground'>
                        {t('common.status')}
                      </span>
                      <Badge
                        variant={data.isActive ? 'default' : 'secondary'}
                        className='px-4 py-2 text-sm font-semibold shadow-sm'
                      >
                        {data.isActive
                          ? t('common.active')
                          : t('common.inactive')}
                      </Badge>
                    </div>
                    <div className='flex flex-col items-start gap-2'>
                      <span className='text-xs font-medium text-muted-foreground'>
                        {t('surveys.privacy')}
                      </span>
                      <Badge
                        variant={data.isAnonymous ? 'outline' : 'secondary'}
                        className='border-2 px-4 py-2 text-sm font-semibold shadow-sm'
                      >
                        {data.isAnonymous
                          ? t('surveys.anonymous')
                          : t('surveys.authenticated')}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Right side - Stats */}
                <div className='flex items-center gap-10'>
                  <div className='text-center'>
                    <div className='mb-2 text-4xl font-bold text-primary drop-shadow-sm'>
                      {data.questions?.length || 0}
                    </div>
                    <div className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                      {t('surveys.questions')}
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='mb-2 text-xl font-bold text-foreground'>
                      {data.createdAt
                        ? new Date(data.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </div>
                    <div className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                      {t('common.createdAt')}
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='mb-2 text-xl font-bold text-foreground'>
                      {data.updatedAt
                        ? new Date(data.updatedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </div>
                    <div className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                      {t('common.updatedAt')}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questions Card */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                {t('surveys.questions')}
                <Badge variant='secondary' className='text-xs'>
                  {data.questions?.length || 0}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.questions && data.questions.length > 0 ? (
                <div className='space-y-4'>
                  {data.questions.map((question, index: number) => (
                    <div
                      key={question.id}
                      className='rounded-lg border bg-muted/20 p-4 transition-colors hover:bg-muted/30'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex-1'>
                          <div className='mb-2 flex items-center gap-2'>
                            <span className='text-sm font-semibold text-primary'>
                              Q{index + 1}
                            </span>
                            <Badge variant='outline' className='text-xs'>
                              {question.type}
                            </Badge>
                            {question.isRequired && (
                              <Badge variant='destructive' className='text-xs'>
                                {t('surveys.required')}
                              </Badge>
                            )}
                          </div>
                          <p className='mb-3 text-sm leading-relaxed text-foreground'>
                            {question.text}
                          </p>

                          {/* Show options for choice questions */}
                          {(question.type === 'SingleChoice' ||
                            question.type === 'MultipleChoice') &&
                            question.options &&
                            question.options.length > 0 && (
                              <div className='space-y-2'>
                                <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                                  {t('surveys.options')}:
                                </div>
                                <div className='flex flex-wrap gap-2'>
                                  {question.options.map(
                                    (option, optionIndex) => (
                                      <div
                                        key={optionIndex}
                                        className='flex w-fit items-center gap-2 rounded-md border bg-background/50 px-3 py-2 text-sm'
                                      >
                                        <div className='flex h-4 w-4 items-center justify-center rounded-full border-2 border-muted-foreground/20'>
                                          {question.type === 'SingleChoice' ? (
                                            <div className='h-2 w-2 rounded-full bg-muted-foreground/40' />
                                          ) : (
                                            <div className='h-2 w-2 rounded border border-muted-foreground/40' />
                                          )}
                                        </div>
                                        <span className='text-foreground'>
                                          {option}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <div className='text-sm text-muted-foreground'>
                    {t('surveys.noQuestionsFound')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

export { FnsDetail }
